/* eslint no-console: 0 */
import * as StackTrace from 'stacktrace-js';
import type { StackFrame as StackFrameJs } from 'stacktrace-js';
import type { ComponentPublicInstance } from 'vue';
import { AxiosError } from 'axios';
import { ErrorRepository } from '@/repositories/ErrorRepository';
import { fsumClient } from './FsumHttpClient';
import type { AjaxProblem } from '@/types/Request/AjaxProblem';

type VueErrorLike = {
    name: string;
    message: string;
    stack: string;
    request?: XMLHttpRequest;
    path?: string;
    httpStatus?: number;
};

function isVueErrorLike(error: unknown): error is VueErrorLike {
    const testError = error as VueErrorLike;
    return testError.name !== undefined && testError.message !== undefined && testError.stack !== undefined;
}

export class GlobalErrorHandler {
    errorRepository: ErrorRepository;

    constructor() {
        this.errorRepository = new ErrorRepository(fsumClient);
        this.setupErrorListeners();
    }

    setupErrorListeners() {
        window.addEventListener(
            'error',
            (error: ErrorEvent) => this.errorEventHandler(error),
        );
        window.addEventListener(
            'unhandledrejection',
            (rejection: PromiseRejectionEvent) => this.rejectionEventHandler(rejection),
        );
    }

    errorEventHandler(errorEvent: ErrorEvent) {
        const {
            message,
            name,
            content,
            stack,
        } = errorEvent.error;

        if (message && stack) {
            this.formatAndSendError(errorEvent.error, message, name, content);
        } else {
            this.formatAndSendLiteral(errorEvent);
        }
    }

    rejectionEventHandler(
        rejectionEvent: PromiseRejectionEvent,
        instance: ComponentPublicInstance|null = null,
        info: string|null = null,
    ) {
        const {
            name,
            message,
            path,
            response,
            stack,
            verb,
        } = rejectionEvent.reason;

        let httpStatus = rejectionEvent.reason.httpStatus ?? null;

        const contentFromInstance = instance?.$options ?? null;
        if (rejectionEvent.reason instanceof AxiosError) {
            httpStatus = rejectionEvent.reason.status ?? rejectionEvent.reason.response?.status;
        }

        if (httpStatus && message) {
            const ajaxProblemDetails = {
                request: {
                    path: path ?? 'Unknown',
                    verb: verb ?? 'Unknown',
                },
                type: rejectionEvent.type ?? '',
                title: name,
                status: httpStatus,
                details: response.details ?? response?.data?.detail ?? '',
                context: response.context ?? { info },
                backtrace: [],
            };

            this.formatAndSendAjaxProblem(rejectionEvent, ajaxProblemDetails, stack);
        } else if (message && stack) {
            this.formatAndSendError(rejectionEvent.reason, message, name, contentFromInstance);
        } else {
            this.formatAndSendUnhandledRejection(rejectionEvent.reason);
        }
    }

    // Can this do the stack building from formatAndSendAjaxProblem?
    vueErrorHandler(
        error: unknown,
        instance: ComponentPublicInstance|null,
        info: string,
    ) {
        if (isVueErrorLike(error)) {
            // build a PromiseRejectionEvent-like object
            const vueError = error as VueErrorLike;

            const errorEvent = {
                reason: {
                    name: vueError.name,
                    message: vueError.message,
                    stack: vueError.stack,
                },
                type: 'vue',
            } as PromiseRejectionEvent;

            // Seems to be a rejected request, add request details
            if (error.request !== undefined && error.request instanceof XMLHttpRequest) {
                errorEvent.reason.request = error.request;
                errorEvent.reason.response = error.request.response;
            }
            if (error.path !== undefined) {
                errorEvent.reason.path = error.path;
            }
            if (error.httpStatus !== undefined) {
                errorEvent.reason.httpStatus = error.httpStatus;
            }

            this.rejectionEventHandler(errorEvent, instance, info);
        }
    }

    formatAndSendAjaxProblem(
        rejectionEvent: PromiseRejectionEvent,
        ajaxProblemDetails: AjaxProblem,
        stack: string|null = null,
    ) {
        // Is stack supposed to be a boolean? Is it only used for the vue errors?
        const resolvedStack = stack
            ?? rejectionEvent.reason instanceof Error ? rejectionEvent.reason.stack : null;

        if (resolvedStack) {
            const ajaxProblemWithBacktrace = ajaxProblemDetails;

            StackTrace.fromError(rejectionEvent.reason)
                .then((stackframes: StackFrameJs[]) => {
                    // map stacktrace-js frames to our DTO
                    ajaxProblemWithBacktrace.backtrace = stackframes.map((stackframe) => (
                        {
                            symbol: stackframe.getFunctionName() ?? '',
                            filename: stackframe.getFileName() ?? '',
                            line: stackframe.getLineNumber() ?? 0,
                            column: stackframe.getColumnNumber() ?? 0,
                        }
                    ));

                    this.errorRepository.sendAjaxProblem(ajaxProblemWithBacktrace)
                        .catch((sendError) => console.error(sendError.message));
                })
                .catch((stackError) => console.error(stackError.message));
        } else {
            this.errorRepository.sendAjaxProblem(ajaxProblemDetails)
                .catch((sendError) => console.error(sendError.message));
        }
    }

    formatAndSendError(
        error: Error,
        message: string,
        name: string|null,
        content: object|null,
    ) {
        StackTrace.fromError(error)
            .then((stackframes: StackFrameJs[]) => {
                // map stacktrace-js frames to our DTO
                const stackDtos = stackframes.map((stackframe) => (
                    {
                        symbol: stackframe.getFunctionName() ?? '',
                        filename: stackframe.getFileName() ?? '',
                        line: stackframe.getLineNumber() ?? 0,
                        column: stackframe.getColumnNumber() ?? 0,
                    }
                ));

                const loggingResponse = this.errorRepository.sendErrorLike({
                    message,
                    name: name ?? 'unknown',
                    content: content ?? null,
                    backtrace: stackDtos,
                });

                loggingResponse.catch((logError: Error) => console.error(logError.message));
            })
            .catch((stackError: Error) => console.error(stackError.message));
    }

    formatAndSendLiteral(errorEvent: ErrorEvent) {
        const { filename, lineno, colno } = errorEvent;

        const loggingResponse = this.errorRepository.sendThrownLiteral({
            message: errorEvent.message ?? errorEvent,
            backtrace: [{
                symbol: 'anonymous',
                filename: filename ?? 'anonymous',
                line: lineno ?? 0,
                column: colno ?? 0,
            }],
        });

        loggingResponse.catch((error: Error) => console.error(error.message));
    }

    formatAndSendUnhandledRejection(rejection: PromiseRejectionEvent) {
        const rejectionContent = Array.isArray(rejection) ? rejection : [rejection];

        const loggingResponse = this.errorRepository.sendUnhandledRejection({
            content: rejectionContent,
        });

        loggingResponse.catch((error: Error) => console.error(error.message));
    }
}
